import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import ModalConfirmation from "../../../common/components/Modal/ModalConfirmation";
import {
  openConfirmationModal,
  closeConfirmationModal,
} from "../../../common/state/slice/modal/modalSlice";
import { uninstallPackage } from "../utils/packageScanner";
import { Spinner } from "./icons";

interface Props {
  packageName: string;
  className?: string;
  onUninstalled?: () => void;
  /** When true, shows a confirmation modal before uninstalling */
  confirmBeforeUninstall?: boolean;
}

const UninstallButton = ({
  packageName,
  className,
  onUninstalled,
  confirmBeforeUninstall = false,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [uninstalling, setUninstalling] = useState(false);

  const modalId = `uninstall-confirm-${packageName}`;

  const doUninstall = async () => {
    if (uninstalling) return;
    setUninstalling(true);
    const success = await uninstallPackage(packageName, dispatch);
    setUninstalling(false);
    if (success && onUninstalled) onUninstalled();
  };

  const handleClick = () => {
    if (confirmBeforeUninstall) {
      dispatch(openConfirmationModal(modalId));
    } else {
      void doUninstall();
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={uninstalling}
        className={
          className ||
          "px-2 py-1.5 text-xs text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-200 rounded-md transition-colors"
        }
      >
        {uninstalling ? <Spinner /> : t("deviceInstaller.uninstall")}
      </button>

      {confirmBeforeUninstall && (
        <ModalConfirmation
          id={modalId}
          title={t("deviceInstaller.uninstallConfirmTitle")}
          text={t("deviceInstaller.uninstallConfirmText", { packageName })}
          submitButtonText={t("deviceInstaller.uninstall")}
          onClickSubmit={() => {
            dispatch(closeConfirmationModal(modalId));
            void doUninstall();
          }}
        />
      )}
    </>
  );
};

export default UninstallButton;
